import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import {
  Paginated,
  PaginationQuery,
  RawPaginationMeta,
  mapPaginationMeta,
} from '../../../../core/models/pagination.model';

export interface ModifierOption {
  id?: string;
  name: string;
  price_delta: string | number;
  sort_order?: number;
  is_active?: boolean;
}

export interface ModifierGroup {
  id: string;
  name: string;
  selection_type: 'single' | 'multiple';
  is_required: boolean;
  min_select: number;
  max_select: number;
  is_active: boolean;
  options: ModifierOption[];
  created_at?: string;
  updated_at?: string;
}

export interface ModifierGroupRequest {
  name: string;
  selection_type: 'single' | 'multiple';
  is_required?: boolean;
  min_select?: number;
  max_select?: number;
  is_active?: boolean;
  options: ModifierOption[];
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: Record<string, string[]>;
}

@Injectable({ providedIn: 'root' })
export class ModifierGroupsService {
  private readonly apiUrl = `${environment.apiUrl}/modifier-groups`;

  constructor(private http: HttpClient) {}

  /** Full (unpaginated) list — used by dropdown consumers (e.g. menu-item form). */
  getAll(): Observable<ApiResponse<ModifierGroup[]>> {
    return this.http.get<ApiResponse<ModifierGroup[]>>(this.apiUrl, {
      params: new HttpParams().set('all', 'true'),
    });
  }

  /** Paginated list for the modifier groups list page. */
  getPage(query: PaginationQuery = {}): Observable<Paginated<ModifierGroup>> {
    const params = new HttpParams()
      .set('page', String(query.page ?? 1))
      .set('perPage', String(query.perPage ?? 25));

    return this.http
      .get<ApiResponse<ModifierGroup[]> & { meta?: RawPaginationMeta }>(this.apiUrl, {
        params,
      })
      .pipe(
        map((res) => ({
          items: res.data ?? [],
          meta: mapPaginationMeta(res.meta, res.data?.length ?? 0),
        })),
      );
  }

  get(id: string): Observable<ApiResponse<ModifierGroup>> {
    return this.http.get<ApiResponse<ModifierGroup>>(`${this.apiUrl}/${id}`);
  }

  create(data: ModifierGroupRequest): Observable<ApiResponse<ModifierGroup>> {
    return this.http.post<ApiResponse<ModifierGroup>>(this.apiUrl, data);
  }

  update(id: string, data: Partial<ModifierGroupRequest>): Observable<ApiResponse<ModifierGroup>> {
    return this.http.put<ApiResponse<ModifierGroup>>(`${this.apiUrl}/${id}`, data);
  }

  delete(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${id}`);
  }

  toggleActive(id: string): Observable<ApiResponse<{ id: string; is_active: boolean }>> {
    return this.http.patch<ApiResponse<{ id: string; is_active: boolean }>>(
      `${this.apiUrl}/${id}/toggle-active`,
      {},
    );
  }
}
